import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { formatCurrency, getSession, saveSession, generateAgreementCode, type Company, type Agreement} from '../lib/session.ts';
import { postJson } from '../lib/api.ts';

// Dinheiro só existe em centavos: a entrada é uma porcentagem do saldo e cai em fração de
// centavo com facilidade. Essa fração ia para o acordo e o pix era gerado no banco com o
// valor arredondado, então o que caía na conta não batia com o valor cobrado na baixa.
const roundToCents = (value: number) => Math.round(value * 100) / 100;

const SimulationPage = () => {
  const { code } = useParams();
  const navigate = useNavigate();
  const [session, setSession] = useState(getSession());
  const [selectedOption, setSelectedOption] = useState<'full' | 'installments'>('full');
  const [installments, setInstallments] = useState(3);
  const [downPaymentRate, setDownPaymentRate] = useState(30);
  const [dueDay, setDueDay] = useState(5);
  const [error, setError] = useState('');
  const [company, setCompany] = useState<Company>();
  const [fullSettlementDiscountRate, setFullSettlementDiscountRate] = useState(0);

  useEffect(() => {
    if (!session || !session.otp || !session.selectedCompany || !session.selectedCharges || !session.selectedCharges.length || !session?.selectedChargesTotalAmount || !session.agreementCode) {
      navigate('/identification');
      return;
    }
    // Sessão anterior à prótese/aparelho no saldo: sem esse valor o desconto sairia sobre
    // tudo, então volta para a lista de dívidas, que preenche a sessão de novo.
    if (session.selectedChargesLabAmount === undefined) {
      navigate(`/charges/${session.selectedCompany.id}`);
    }
  }, [navigate, session]);

  useEffect(() => {
    if (!session?.selectedCompany || !session?.customerId) return;
    setCompany(session?.selectedCompany);
    setFullSettlementDiscountRate(session?.selectedCompany?.configs?.payment.full_settlement_discount_rate || 0);
  }, [session?.agreementCode, session?.customerId]);

  const balance = session?.selectedChargesTotalAmount || 0;
  // Prótese e aparelho complementar entram no acordo pelo valor de face: o desconto de
  // quitação só alcança a dívida do tratamento.
  const labBalance = session?.selectedChargesLabAmount || 0;
  const fullPriceDiscounted = roundToCents(
    (balance - labBalance) * (1 - fullSettlementDiscountRate / 100) + labBalance
  );
  const minimumRate = company?.configs.payment.minimum_down_payment_rate || 0;
  const minimumInstallment = company?.configs.payment.minimum_installment_amount || 0;
  // A entrada não pode deixar a parcelar menos do que uma parcela mínima: R$180 de dívida
  // com R$100 de entrada deixaria uma parcela de R$80, abaixo do mínimo.
  const maximumRate = balance > 0 ? Math.floor(((balance - minimumInstallment) / balance) * 100) : 0;
  const canInstall = balance > 0 && maximumRate >= minimumRate;
  const effectiveRate = canInstall ? Math.min(Math.max(downPaymentRate, minimumRate), maximumRate) : minimumRate;
  const downPayment = company ? roundToCents((effectiveRate / 100) * balance) : 0;

  // O máximo de parcelas depende do saldo a parcelar: parcela não pode ficar abaixo do
  // mínimo da empresa, senão o acordo somaria mais do que a dívida.
  const financedBalance = balance - downPayment;
  const maximumInstallments = company
    ? Math.max(
        1,
        Math.min(
          company.configs.payment.maximum_installments,
          minimumInstallment > 0 ? Math.floor(financedBalance / minimumInstallment) : company.configs.payment.maximum_installments
        )
      )
    : 1;
  const installmentsAmount = company ? Math.ceil((financedBalance / installments) * 100) / 100 : 0;

  // Mais entrada deixa menos a parcelar, e o máximo cai: os sliders acompanham.
  useEffect(() => {
    if (installments > maximumInstallments) setInstallments(maximumInstallments);
  }, [maximumInstallments]);

  useEffect(() => {
    if (downPaymentRate !== effectiveRate) setDownPaymentRate(effectiveRate);
  }, [effectiveRate]);

  // Saldo menor que uma parcela mínima: só à vista.
  useEffect(() => {
    if (!canInstall && selectedOption === 'installments') setSelectedOption('full');
  }, [canInstall]);

  const limitedByMinimum =
    selectedOption === 'installments' && company && maximumInstallments < company.configs.payment.maximum_installments;

  const handleContinue = async () => {
    const agreementCode = code || session?.agreementCode || generateAgreementCode();
    const downPaymentAmount = selectedOption === 'full' ? fullPriceDiscounted : downPayment;
    const installmentAmount = selectedOption === 'full' ? 0 : installmentsAmount;
    const installmentsValue = selectedOption === 'full' ? 0 : installments;
    const dueDayValue = selectedOption === 'full' ? 0 : dueDay;

    try {
      const agreement = {
        code: agreementCode,
        down_payment_amount: downPaymentAmount,
        installments: installmentsValue,
        installment_amount: installmentAmount,
        due_day: dueDayValue,
        charges: session?.selectedCharges,
      };

      try {
        await postJson<Agreement>(
          `/api/charges/${session?.customerId}/agreement/${agreementCode}`, agreement
        );
      } catch (error) {
        console.error(error);
        setError((error as Error).message || 'Erro ao registrar acordo.');
        return;
      }

      const updatedSession: any = {
        ...session,
        agreement,
        agreementCode,
        paymentAmount: downPaymentAmount,
      };

      saveSession(updatedSession);
      setSession(updatedSession);
      navigate(`/agreement/${agreementCode}/payment`);
    } catch (error) {
      setError((error as Error).message);
    }
  };

  if (!company) return null;

  return (
    <main className="space-y-8">
      <section className="rounded-3xl bg-white p-8 shadow-md shadow-slate-200">
        <p className="text-sm uppercase tracking-[0.3em] text-red-600">Simulação de acordo</p>
        <h1 className="mt-4 text-3xl font-semibold text-red-600">Pague menos se escolher à vista</h1>
        <p className="mt-3 text-slate-600">O melhor caminho para ficar em dia é fechar o saldo agora com desconto ou parcelar com entrada mínima.</p>
        <p className="mt-4 text-sm text-slate-500">Saldo a renegociar: {formatCurrency(balance)}</p>
        {labBalance > 0 && (
          <p className="text-sm text-slate-500">
            Inclui {formatCurrency(labBalance)} de prótese e/ou aparelho complementar, que entra no acordo sem desconto.
          </p>
        )}
      </section>

      <section className="grid gap-4">
        <button
          type="button"
          onClick={() => setSelectedOption('full')}
          className={`rounded-3xl border p-6 text-left shadow-sm transition ${selectedOption === 'full' ? 'bg-red-200 hover:bg-red-400 border-red-250 text-white' : 'border-slate-200 bg-white text-slate-600'}`}
        >
          <div className="flex items-center justify-between gap-4">
            <div>
              <p className="text-sm uppercase tracking-[0.35em]">À vista</p>
              <p className="mt-3 text-3xl font-semibold">{formatCurrency(fullPriceDiscounted)}</p>
            </div>
            <span className="rounded-full bg-red-100 px-3 py-1 text-xs font-semibold uppercase tracking-[0.35em] text-slate-600">Economize {company.configs.payment.full_settlement_discount_rate}%</span>
          </div>
          <p className="mt-4 text-sm text-slate-500">Pague o total agora e garanta o menor valor. Recomendado sempre que possível.</p>
          {labBalance > 0 && (
            <p className="mt-2 text-sm text-slate-500">
              O desconto não se aplica a prótese e aparelho complementar ({formatCurrency(labBalance)}).
            </p>
          )}
        </button>

        <button
          type="button"
          disabled={!canInstall}
          onClick={() => setSelectedOption('installments')}
          className={`rounded-3xl border p-6 text-left shadow-sm transition ${selectedOption === 'installments' ? 'bg-red-200 hover:bg-red-400 border-red-250 text-white' : 'border-slate-200 bg-white text-slate-600'}`}
        >
          <div className="flex items-center justify-between gap-4">
            <div>
              <p className="text-sm uppercase tracking-[0.35em]">Renegociar</p>
              <p className="mt-3 text-3xl font-semibold">{formatCurrency(balance)}</p>
            </div>
            <span className="rounded-full bg-red-100 px-3 py-1 text-xs font-semibold uppercase tracking-[0.35em] text-slate-600">até {maximumInstallments}x</span>
          </div>
          <p className="mt-4 text-sm text-slate-500">
            {canInstall
              ? `Entrada mínima de ${minimumRate}% e parcelas a partir de ${formatCurrency(minimumInstallment)}.`
              : `Saldo menor que a parcela mínima de ${formatCurrency(minimumInstallment)}: só à vista.`}
          </p>
          {labBalance > 0 && (
            <p className="mt-2 text-sm text-slate-500">
              Inclui prótese e/ou aparelho complementar: {formatCurrency(labBalance)}
            </p>
          )}
        </button>
      </section>

      {selectedOption === 'installments' && (
        <section className="rounded-3xl bg-white p-6 shadow-sm">
          <div className="flex items-center justify-between gap-4">
            <div>
              <p className="text-sm text-slate-500">Entrada</p>
              <p className="mt-2 text-2xl font-semibold text-slate-600">{formatCurrency(downPayment)}</p>
            </div>
            <div>
              <p className="text-sm text-slate-500">Parcelas</p>
              <p className="mt-2 text-2xl font-semibold text-slate-600">{installments}x {formatCurrency(installmentsAmount)}</p>
            </div>
          </div>

          <div className="mt-6 grid gap-6 sm:grid-cols-2">
            <label className="space-y-3">
              <span className="text-sm text-slate-600">Percentual de entrada</span>
              <input
                type="range"
                min={minimumRate}
                max={maximumRate}
                value={downPaymentRate}
                onChange={(event) => setDownPaymentRate(Number(event.target.value))}
                className="w-full"
              />
              <p className="text-sm text-slate-500">{downPaymentRate}% do saldo</p>
            </label>
            <label className="space-y-3">
              <span className="text-sm text-slate-600">Número de parcelas</span>
              <input
                type="range"
                min={1}
                max={maximumInstallments}
                value={installments}
                onChange={(event) => setInstallments(Number(event.target.value))}
                className="w-full"
              />
              <p className="text-sm text-slate-500">{installments} parcelas</p>
            </label>
            <label className="space-y-3">
              <span className="text-sm text-slate-600">Dia de vencimento das parcelas</span>
              <input
                type="range"
                min={1}
                max={28}
                value={dueDay}
                onChange={(event) => setDueDay(Number(event.target.value))}
                className="w-full"
              />
              <p className="text-sm text-slate-500">Todo dia {dueDay}</p>
            </label>
          </div>

          {limitedByMinimum && (
            <p className="mt-4 rounded-2xl bg-amber-50 p-4 text-sm text-amber-800">
              Com esta entrada, o máximo é {maximumInstallments}x para manter a parcela mínima de {formatCurrency(minimumInstallment)}.
            </p>
          )}
        </section>
      )}

      {error && <div className="rounded-2xl bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>}

      <button
        onClick={handleContinue}
        className="w-full rounded-2xl bg-red-600 text-white px-5 py-4 text-sm font-semibold shadow-lg transition hover:bg-red-700"
      >
        Continuar com a opção {selectedOption === 'full' ? 'à vista' : 'de renegociação'}
      </button>
    </main>
  );
};

export default SimulationPage;
